// Usage: wrap your component which depends on the viewport using the appropriate values for the
// 'showIn' array. Available modes: mobile, tablet, desktop
// <ResponsiveView showIn=['desktop']><MyDesktopComponent /></ResponsiveView>

import React from 'react';
import PropTypes from 'prop-types';
import { throttle } from 'lodash';

const MOBILE_MAX_WIDTH = 500;
const TABLET_MAX_WIDTH = 1023;
const MODES = ['mobile', 'tablet', 'desktop'];

export default class ResponsiveView extends React.Component {

  constructor() {
    super();
    this.state = { width: window.innerWidth };

    // Reference: https://stackoverflow.com/questions/38564080/remove-event-listener-on-unmount-react
    this.onResize = throttle(this.onResize.bind(this), 1000);
  }

  componentDidMount() {
    window.addEventListener("resize", this.onResize, false);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.onResize, false);
  }

  onResize() {
    this.setState({width: window.innerWidth});
  }

  shouldDisplay(mode, width) {
    switch ( mode ) {
      case 'mobile':
        return width <= MOBILE_MAX_WIDTH;
      case 'tablet':
        return width <= TABLET_MAX_WIDTH && width > MOBILE_MAX_WIDTH;
      case 'desktop':
        return width > TABLET_MAX_WIDTH;
      default:
        return false;
    }
  }

  isModeValid(mode, width) {
    return MODES.indexOf(mode) > -1 && this.shouldDisplay(mode, width);
  }

  render() {
    const { showIn, children } = this.props;
    const width = window.innerWidth;

    if ( typeof showIn === 'string' ) {
      if ( this.isModeValid(showIn, width) ) return children;
    } else {
      let shouldDisplay = false;
      showIn.forEach(mode => {
        if ( this.isModeValid(mode, width) ) shouldDisplay = true;
      });
      if ( shouldDisplay ) return children;
    }

    return null;
  }
}

ResponsiveView.propTypes = {
  children: PropTypes.object.isRequired,
  showIn: (props, propName, componentName) => {
    const showIn = props[propName];

    if ( typeof showIn === "undefined" ) {
      return new Error(`Required prop '${propName}' in '${componentName}': `
          + `Must be a subset of ${MODES} but got ${showIn}.`);
    }

    const validateMode = (mode) => {
      if ( MODES.indexOf(mode) === -1 ) {
        return new Error(`Invalid prop '${propName}' supplied to '${componentName}': `
          + `Must be a subset of ${MODES} but got ${mode}.`);
      }
    }

    if ( typeof showIn === "string" ) validateMode(showIn);
    else showIn.forEach(mode => validateMode(mode));
  }
};
